import { capitalIpa } from "../phonemes/cleanupIpa";
import { Feature, getFeatureSet } from "../phonemes/features";
import { getAllIpa, getFeaturesForOne, IpaType, syllableBoundary, wordBoundary } from "../phonemes/ipa";
import { segmentPhonemes } from "../phonemes/segmentPhonemes";
import { Pattern } from "./pattern";
import { FeatureItem, PhonemeInfo } from "./phonemeInfo";

export function stringToPattern(
  s: string
): Pattern<PhonemeInfo> | PatternError {
  const result = parseAny(s, 0, 0);
  return result === 'done' ? { error: 'empty', index: 0 }
    : 'error' in result ? result
      : result[0];
}

export interface PatternError {
  readonly error:
  | 'empty' | 'invalid feature' | 'incomplete'
  | 'missing ]' | 'missing )' | 'missing }' | 'missing >'
  | '{} requires & or |';
  readonly index: number;
  readonly details?: string;
}

/** parse whatever's next: seq, opt, ipa, etc. */
function parseAny(
  s: string, i: number, globalI: number
): [Pattern<PhonemeInfo>, number] | PatternError | 'done' {
  const items: Pattern<PhonemeInfo>[] = [];
  while (i < s.length) {
    const result = parseOne(s, i, globalI);
    if (result === 'done') {
      break;
    } else if ('error' in result) {
      return result;
    } else if (result[1] === 0) {
      break;  // stop if we didn't advance
    }
    items.push(result[0]);
    i += result[1];
  }

  switch (items.length) {
    case 0: return { error: 'incomplete', index: globalI };
    case 1: return [items[0], i];
    default: return [{ seq: items }, i];
  }
}

/** figure out what the next thing is & parse just it (not a seq) */
function parseOne(
  s: string, i: number, globalI: number
): [Pattern<PhonemeInfo>, number] | PatternError | 'done' {
  switch (s[i]) {
    case undefined: return 'done';
    case wordBoundary: return [{ special: 'word' }, i + 1];
    case syllableBoundary: return [{ special: 'syllable' }, i + 1];
    case '∅': return [{ empty: '' }, i + 1];
    case '[': return parseFeatures(s, i, globalI);
    case '{': return parseAndOr(s, i, globalI);
    case '(': return parseOpt(s, i, globalI);
    case '<': return parseStore(s, i, globalI);
    case '-': return parseNot(s, i, globalI);
    default: return parseIpaOrName(s, i, globalI);
  }
}

/** grab the comma delimited values in [] & validate they're features */
function parseFeatures(
  s: string, i: number, globalI: number
): [FeatureItem, number] | PatternError {
  const end = s.indexOf(']', i);
  if (end === -1) {
    return { error: 'missing ]', index: i };
  }

  const substr = s.substring(i + 1, end);
  const items = substr.split(',');
  for (const f of items) {
    if (!isValidFeature(f)) {
      return { error: 'invalid feature', index: globalI, details: f };
    }
  }
  return [{ feature: items as Feature[] }, end - i + 1];
}

/** parse items delimited by {} used by AND and OR */
function parseAndOr(
  s: string, i: number, globalI: number
): [Pattern<PhonemeInfo>, number] | PatternError {
  const end = s.indexOf('}', i);
  if (end === -1) {
    return { error: 'missing }', index: globalI };
  }

  const substr = s.substring(i + 1, end);
  const isAnd = substr.includes('&');
  const isOr = substr.includes('|');
  if (!isAnd && !isOr) {
    return { error: '{} requires & or |', index: globalI };
  } else if (isAnd && isOr) {
    return { error: '{} requires & or |', index: globalI };
  }

  return parseAndOrBody(substr, isAnd, end - i + 1, globalI);
}
/** parse AND or OR without wrapping delimiters */
function parseAndOrBody(
  s: string, isAnd: boolean, length: number, globalI: number
): [Pattern<PhonemeInfo>, number] | PatternError {
  const items: Pattern<PhonemeInfo>[] = [];
  const strings = s.split(isAnd ? '&' : '|');
  for (const s of strings) {
    const inner = parseAny(s.trim(), 0, globalI);
    if (inner === 'done') {
      return { error: 'incomplete', index: globalI };
    } if ('error' in inner) {
      return inner;
    }
    items.push(inner[0]);
  }
  return [isAnd ? { and: items } : { or: items }, length];
}

/** parse items delimited by () indicating they're optional */
function parseOpt(
  s: string, i: number, globalI: number
): [Pattern<PhonemeInfo>, number] | PatternError {
  const end = s.indexOf(')', i);
  if (end === -1) {
    return { error: 'missing )', index: i };
  }

  const substr = s.substring(i + 1, end);
  const isAnd = substr.includes('&');
  const isOr = substr.includes('|');
  // if there's an & or |, we don't need an extra nested {}
  const inner = isAnd || isOr
    ? parseAndOrBody(substr, isAnd, end - i + 1, globalI)
    : parseAny(substr, 0, globalI + 1);
  if (inner === 'done') {
    return { error: 'incomplete', index: globalI };
  } if ('error' in inner) {
    return inner;
  }
  return [{ opt: inner[0] }, end - i + 1];
}

/** parse items delimited by <> indicating to store the match */
function parseStore(
  s: string, i: number, globalI: number
): [Pattern<PhonemeInfo>, number] | PatternError {
  const end = s.indexOf('>', i);
  if (end === -1) {
    return { error: 'missing >', index: i };
  }

  const substr = s.substring(i + 1, end);
  const isAnd = substr.includes('&');
  const isOr = substr.includes('|');
  // if there's an & or |, we don't need an extra nested {}
  const inner = isAnd || isOr
    ? parseAndOrBody(substr, isAnd, end - i + 1, globalI)
    : parseAny(substr, 0, globalI + 1);
  if (inner === 'done') {
    return { error: 'incomplete', index: i };
  } if ('error' in inner) {
    return inner;
  }
  return [{ store: inner[0] }, end - i + 1];
}

/** negate whatever item is found */
function parseNot(
  s: string, i: number, globalI: number
): [Pattern<PhonemeInfo>, number] | PatternError {
  const inner = parseAny(s, i + 1, globalI + 1);
  if (inner === 'done') {
    return { error: 'incomplete', index: i };
  } if ('error' in inner) {
    return inner;
  }
  return [{ not: inner[0] }, inner[1]];
}

/** upper case is considered a name, valid IPA is ipa */
function parseIpaOrName(
  s: string, i: number, globalI: number
): [Pattern<PhonemeInfo>, number] | 'done' {
  // anything "upper case" is considered to be a name
  const a = s[i];
  if (('A' <= a && a <= 'Z') || getCapitalIpaSet().has(a)) {
    return [{ name: a }, 1];
  }

  // grab the next stretch of valid IPA
  const end = findFirstNonIpa(s, i);
  if (end === -1) {
    return 'done';
  }
  const substr = s.substring(i, end);
  const list = segmentPhonemes(substr);
  const ipa = list.phonemes.map(p => p.phoneme);
  return [{ ipa }, end - i];
}

/** return first non-ipa after s[i] or -1 if none */
function findFirstNonIpa(s: string, i: number): number {
  const ipa = getIpaSet();
  let hasOne = false;
  for (; i < s.length; i++) {
    if (!ipa.has(s[i])) {
      return i;
    }
    hasOne = true;
  }
  return hasOne ? s.length : -1;
}

/** cache all valid IPA in a Set<> */
function getIpaSet(): Set<IpaType> {
  if (ipaSet.size === 0) {
    const { ipa, diacritics } = getAllIpa();
    ipa.forEach(p => ipaSet.add(p));
    diacritics.forEach(p => ipaSet.add(p));

    // but without delimiters
    ipa.forEach(i => {
      const features = getFeaturesForOne(i);
      if (features.includes('delimit')) {
        ipaSet.delete(i);
      }
    });
  }
  return ipaSet;
}
const ipaSet = new Set<IpaType>();

/** check if this is a valid feature */
function isValidFeature(feature: string): boolean {
  const featureSet = getFeatureSet();
  return featureSet.has(feature as Feature);
}

/** cache captial IPA in a Set<> */
function getCapitalIpaSet(): Set<IpaType> {
  if (capitalIpaSet.size === 0) {
    capitalIpa.forEach(p => capitalIpaSet.add(p));
  }
  return capitalIpaSet;
}
const capitalIpaSet = new Set<IpaType>();
